/**
 * After the sale: cancellation reasons, returns, refunds and replacements -
 * the policy page, the request queue, the order page's requests and the
 * product form's return rules. Conventions: see `common.ts`.
 */
const afterSale: Record<string, string> = {
  // >>> shared words
  reason_other: 'Other',
  'kind.CANCELLATION': 'Partial cancellation',
  'kind.RETURN': 'Return',
  'kind.REFUND': 'Refund',
  'kind.REPLACEMENT': 'Replacement',
  'status.REQUESTED': 'Waiting for you',
  'status.APPROVED': 'Approved',
  'status.REJECTED': 'Rejected',
  'status.COMPLETED': 'Completed',
  'refund_method.ORIGINAL_PAYMENT': 'Back to the original payment',
  'refund_method.MANUAL': 'I will pay it back myself',
  'refund_method.STORE_CREDIT': 'Store credit (a gift card)',
  'cancelled_by.CUSTOMER': 'The customer cancelled this order: {reason}',
  'cancelled_by.MERCHANT': 'Cancelled by your store. The customer was told: {reason}',
  'cancelled_by.SYSTEM': 'Cancelled automatically: {reason}',
  'item.cancelled': '{count} cancelled',
  'item.returned': '{count} returned',
  'item.replaced': '{count} replaced',

  // >>> policy page
  'policy.title': 'Returns & cancellations',
  'policy.intro':
    'What your customers can ask for after they order: cancelling, and - once it arrives - a return, a refund or a replacement.',
  'policy.loading': 'Loading your policy…',
  'policy.saved': 'Saved. New requests follow these rules.',
  'policy.errors.generic': 'Could not save your policy. Please try again.',
  'policy.errors.window': 'Enter whole days from 1 to 365.',
  'policy.empty_title': 'Set up your store first',
  'policy.empty_description': 'Your returns policy belongs to your store, so create the store to set it.',
  'policy.empty_action': 'Create your store',
  'policy.cancel_title': 'Cancellations',
  'policy.cancel_description':
    'Customers cancel from their order page. They always choose a reason, and you see it.',
  'policy.customer_cancellation': 'Customers can cancel their own orders',
  'policy.customer_cancellation_hint': 'When this is off, they have to contact you to cancel.',
  'policy.cancellable_statuses': 'They can cancel while the order is',
  'policy.partial_cancellation': 'Let customers cancel some items instead of the whole order',
  'policy.partial_cancellation_hint': 'Each partial cancellation waits for your approval.',
  'policy.cancellation_reasons': 'Reasons customers choose from when cancelling',
  'policy.after_delivery_title': 'After delivery',
  'policy.after_delivery_description':
    'What customers can ask for once the order has been delivered. You can switch any of these off for single products.',
  'policy.returns': 'Returns',
  'policy.returns_hint': 'The item comes back to you, then you refund it.',
  'policy.refunds': 'Refunds without a return',
  'policy.refunds_hint': 'Money back, and the customer keeps the item - for damage not worth shipping.',
  'policy.replacements': 'Replacements',
  'policy.replacements_hint': 'The item comes back and you send a new one. No money moves.',
  'policy.window': 'Days after delivery to ask',
  'policy.window_hint': 'A product can have its own number of days.',
  'policy.allow_photos': 'Customers can add photos',
  'policy.allow_photos_hint': 'Up to 5 per request - useful for damaged items.',
  'policy.return_reasons': 'Reasons customers choose from for a return, refund or replacement',
  'policy.policy_note': 'Note shown to customers on the request form',
  'policy.policy_note_placeholder': 'Items must be unused, with tags, in their original packaging.',
  'policy.settling_title': 'Settling requests',
  'policy.settling_description': 'How money goes back, and whether requests need your approval.',
  'policy.refund_method': 'How refunds are paid',
  'policy.refund_method_hint.ORIGINAL_PAYMENT':
    'Online payments are refunded through Razorpay. Cash on delivery orders are marked for you to pay back yourself.',
  'policy.refund_method_hint.MANUAL':
    'Nothing is sent automatically. The order records the amount you owe the customer.',
  'policy.refund_method_hint.STORE_CREDIT':
    'The customer gets a gift card for the amount, to spend in your store.',
  'policy.auto_approve': 'Approve requests automatically',
  'policy.auto_approve_hint':
    'You still complete each one: confirm the item came back, or send the replacement.',
  'policy.summary_title': 'Your policy',
  'policy.summary_cancel_on': 'Customers can cancel from their order page.',
  'policy.summary_cancel_off': 'Customers contact you to cancel.',
  'policy.summary_after_on': 'Customers can ask within {days} days of delivery.',
  'policy.summary_after_off': 'No returns, refunds or replacements are offered.',
  'policy.summary_products':
    'Orders keep the rules they were placed under, so tightening them later never takes a promise back.',
  'policy.manage_products': 'Set rules for single products',
  'policy.read_only': 'Only people who can edit the store can change this policy.',
  'policy.new_reason': 'New reason',
  'policy.add_reason': 'Add reason',
  'policy.add_reason_placeholder': 'Size or fit is not right',
  'policy.remove_reason': 'Remove {reason}',

  // >>> cancelling from the dashboard
  'cancel.title': 'Cancel {order}',
  'cancel.title_bulk': 'Cancel {count} orders',
  'cancel.description':
    'Stock goes back on sale and any gift card is credited back. The customer is emailed the reason below, and sees it on their order page.',
  'cancel.reason': 'Reason',
  'cancel.details': 'Message for the customer (optional)',
  'cancel.details_required': 'Reason for the customer',
  'cancel.details_placeholder': 'Sorry - the last one was damaged in our warehouse.',
  'cancel.too_short': 'Write at least a few words.',
  'cancel.customer_sees': 'The customer sees',
  'cancel.keep': 'Keep the order',
  'cancel.confirm': 'Cancel order',
  'cancel.cancelling': 'Cancelling…',
  'merchant_reason.out_of_stock': 'The item is out of stock',
  'merchant_reason.cannot_deliver': 'We cannot deliver to this address',
  'merchant_reason.payment_not_received': 'Payment was not received',
  'merchant_reason.customer_asked': 'Cancelled at your request',
  'merchant_reason.suspected_fraud': 'We could not verify this order',
  'merchant_reason.other': 'Something else',

  // >>> requests on an order
  'request.card_title': 'Returns & requests',
  'request.open_on_order': 'Requests waiting on this order: {count}. See Returns & requests below.',
  'request.by_CUSTOMER': 'Asked by the customer',
  'request.by_MERCHANT': 'Recorded by your team',
  'request.reason_line': 'Reason: {reason}',
  'request.comment_line': 'Customer wrote: {comment}',
  'request.answer_line': 'Your reply: {note}',
  'request.photo_alt': 'Photo from the customer',
  'request.refunded_original': '{amount} refunded to the original payment.',
  'request.refunded_manual': '{amount} to pay back to the customer yourself.',
  'request.refunded_credit': '{amount} issued as store credit - gift card {code}.',

  // >>> deciding a request
  'decision.button_approve': 'Approve',
  'decision.button_reject': 'Reject',
  'decision.button_complete': 'Complete',
  'decision.title_approve': 'Approve {kind}',
  'decision.title_reject': 'Reject {kind}',
  'decision.title_complete': 'Complete {kind}',
  'decision.confirm_approve': 'Approve',
  'decision.confirm_reject': 'Reject request',
  'decision.confirm_complete': 'Mark completed',
  'decision.working': 'Saving…',
  'decision.error': 'Could not update the request. Please try again.',
  'decision.explain_approve_CANCELLATION':
    'These items come off the order now: stock goes back on sale and the refund below is sent.',
  'decision.explain_approve_RETURN':
    'The customer is told to send the items back. Complete the return when they arrive, and the refund is sent then.',
  'decision.explain_approve_REFUND':
    'The customer keeps the items and the refund below is sent now.',
  'decision.explain_approve_REPLACEMENT':
    'The customer is told to send the items back. Complete it when you dispatch the new ones.',
  'decision.explain_reject_CANCELLATION': 'The items stay on the order. Tell the customer why.',
  'decision.explain_reject_RETURN': 'Nothing comes back and nothing is refunded. Tell the customer why.',
  'decision.explain_reject_REFUND': 'Nothing is refunded. Tell the customer why.',
  'decision.explain_reject_REPLACEMENT': 'No replacement is sent. Tell the customer why.',
  'decision.explain_complete_CANCELLATION':
    'These items come off the order: stock goes back on sale and the refund below is sent.',
  'decision.explain_complete_RETURN':
    'The items are back: stock goes back on sale and the refund below is sent.',
  'decision.explain_complete_REFUND': 'The refund below is sent.',
  'decision.explain_complete_REPLACEMENT':
    'The new items are on their way: they come off your stock. Nothing is refunded.',
  'decision.refund_amount': 'Refund amount (₹)',
  'decision.amount_invalid': 'Use a number like 499 or 499.50',
  'decision.suggested': 'Suggested: {amount} - these items’ share of what was paid, without delivery.',
  'decision.note': 'Message for the customer (optional)',
  'decision.note_required': 'Why are you rejecting it? The customer sees this.',
  'decision.note_placeholder_approve': 'Please pack it in the original box.',
  'decision.note_placeholder_reject': 'The tags have been removed, so we cannot take it back.',
  'decision.note_placeholder_complete': 'Received today - your refund is on its way.',

  // >>> the request queue
  'queue.title': 'Returns & requests',
  'queue.intro':
    'Cancellations of single items, returns, refunds and replacements your customers have asked for.',
  'queue.policy_link': 'Returns policy',
  'queue.tabs_label': 'Request views',
  'queue.tab.open': 'To do',
  'queue.tab.REQUESTED': 'New',
  'queue.tab.APPROVED': 'Approved',
  'queue.tab.COMPLETED': 'Completed',
  'queue.tab.REJECTED': 'Rejected',
  'queue.tab.all': 'All',
  'queue.kind_filter': 'Type',
  'queue.all_kinds': 'All types',
  'queue.loading': 'Loading requests…',
  'queue.load_failed': 'Could not load requests.',
  'queue.empty_title': 'Nothing here',
  'queue.empty_description': 'When a customer asks for a return, refund or replacement, it appears here.',
  'queue.would_refund': 'would refund {amount}',

  // >>> the product form
  'product.section': 'Returns',
  'product.section_description':
    'By default this product follows your store’s returns policy. Set its own rules to narrow it - for example, non-returnable.',
  'product.custom': 'Use rules just for this product',
  'product.custom_hint': 'Off follows your store’s returns policy.',
  'product.returnable': 'Returnable',
  'product.refundable': 'Refundable without a return',
  'product.replaceable': 'Replaceable',
  'product.window': 'Days after delivery to ask',
  'product.window_placeholder': 'Store default',
  'product.window_hint': 'Leave empty to use your store’s number of days.',
  'product.ceiling_note':
    'Anything your store policy switches off stays off - a product can only offer less than your store does.',

  // >>> bulk, from the product list
  'bulk.button': 'Returns',
  'bulk.title': 'Returns for {count} products',
  'bulk.description': 'Give every selected product the same return rules.',
  'bulk.apply': 'Apply',
  'bulk.error': 'Could not update the products.',
  'bulk.done': 'Returns updated on {count} products.',
};

export default afterSale;
